/** Send for Signature — BoldSign embedded signing, v1 expenses-calendar-
 * materials.js esignOpen/esignUpload rebuilt for v2. On mount it hits the config
 * gate; without BOLDSIGN_API_KEY on the server it shows the "add the key" hint
 * and a Close button (never crashes). Configured: a document title, a recipients
 * list (name + email, add/remove, at least one), and a PDF → POST
 * /api/boldsign/embed-new → the returned sendUrl fills the modal in an iframe. */
import { useEffect, useState } from "react";
import { esignEmbedNew, fetchEsignConfig, type EsignRecipient } from "./api";

type Phase =
  | { kind: "loading" }
  | { kind: "notconfig" }
  | { kind: "form" }
  | { kind: "sending" }
  | { kind: "embed"; url: string };

const CONFIG_HINT =
  "E-signature connects through BoldSign on the backend — set BOLDSIGN_API_KEY as a server secret to enable.";

export function EsignModal({
  token,
  address,
  onClose,
}: {
  token: string | null;
  address: string;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [title, setTitle] = useState(`${address} — Agreement`);
  const [recipients, setRecipients] = useState<EsignRecipient[]>([{ name: "", email: "" }]);
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // gate on the server config (needs BOLDSIGN_API_KEY) — v1 esignOpen's first step
  useEffect(() => {
    const ctl = new AbortController();
    let alive = true;
    void fetchEsignConfig(token, { signal: ctl.signal }).then((ok) => {
      if (alive) setPhase(ok ? { kind: "form" } : { kind: "notconfig" });
    });
    return () => {
      alive = false;
      ctl.abort();
    };
  }, [token]);

  // Escape closes the modal (but not while the embed builder owns the keys).
  // The drawer underneath suppresses its own Escape handler while esignOpen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && phase.kind !== "embed") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, phase.kind]);

  const busy = phase.kind === "sending";
  const setRcpt = (i: number, patch: Partial<EsignRecipient>) =>
    setRecipients((prev) => prev.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const addRcpt = () => setRecipients((prev) => [...prev, { name: "", email: "" }]);
  const removeRcpt = (i: number) => setRecipients((prev) => prev.filter((_, k) => k !== i));

  const validRecipients = recipients.filter((r) => r.email.includes("@"));
  const canSend = phase.kind === "form" && file !== null && validRecipients.length > 0 && title.trim() !== "";

  const send = () => {
    if (!file) {
      setErr("Choose a PDF to send for signature.");
      return;
    }
    if (validRecipients.length === 0) {
      setErr("Add at least one recipient with an email address.");
      return;
    }
    setErr(null);
    setPhase({ kind: "sending" });
    void esignEmbedNew(token, {
      file,
      title: title.trim() || "Document",
      leadKey: address,
      recipients: validRecipients,
    })
      .then((url) => setPhase({ kind: "embed", url }))
      .catch((e: unknown) => {
        setErr(e instanceof Error ? e.message : "Could not prepare the document — check the backend.");
        setPhase({ kind: "form" });
      });
  };

  const embed = phase.kind === "embed";

  return (
    <div className="cmpmodalscrim" onClick={onClose}>
      <div className={"cmpmodal esignmodal" + (embed ? " embed" : "")} onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody esignbody">
          <div className="cmpmodalhead">
            <h3>
              📝 Send for Signature <span className="esignsub">— {address.split(",")[0]}</span>
            </h3>
            <button className="close" onClick={onClose}>×</button>
          </div>

          {phase.kind === "loading" ? (
            <div className="esignmsg sub">Checking e-signature setup…</div>
          ) : null}

          {phase.kind === "notconfig" ? (
            <div className="esignnote">
              <p className="sub">{CONFIG_HINT}</p>
              <div className="expformacts">
                <button className="btn slim" onClick={onClose}>Close</button>
              </div>
            </div>
          ) : null}

          {phase.kind === "form" || busy ? (
            <div className="esignform">
              <p className="esignlede sub">
                Upload a PDF to prepare &amp; send with BoldSign — add recipients, then place the
                signature / date / text fields in the builder.
              </p>

              <label className="lbl">Document title</label>
              <input
                className="moneyinput"
                value={title}
                placeholder="Agreement"
                disabled={busy}
                onChange={(e) => setTitle(e.target.value)}
              />

              <label className="lbl">Recipients — who signs this document</label>
              <div className="esignrcpts">
                {recipients.map((r, i) => (
                  <div key={i} className="esignrcpt">
                    <span className="esignrnum">{i + 1}</span>
                    <input
                      className="moneyinput"
                      value={r.name}
                      placeholder="Name"
                      disabled={busy}
                      onChange={(e) => setRcpt(i, { name: e.target.value })}
                    />
                    <input
                      className="moneyinput"
                      type="email"
                      value={r.email}
                      placeholder="email@example.com"
                      disabled={busy}
                      onChange={(e) => setRcpt(i, { email: e.target.value })}
                    />
                    {recipients.length > 1 ? (
                      <button className="rowbtn del" title="Remove recipient" disabled={busy} onClick={() => removeRcpt(i)}>✕</button>
                    ) : null}
                  </div>
                ))}
                <button className="dlink esignadd" disabled={busy} onClick={addRcpt}>+ Add another recipient</button>
              </div>

              <label className="lbl">Document (PDF)</label>
              <label className={"esigndrop" + (file ? " has" : "")}>
                <input
                  type="file"
                  accept="application/pdf"
                  disabled={busy}
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null);
                    setErr(null);
                  }}
                />
                <span className="esigndropi">⬆</span>
                <span className="esigndropt">{file ? file.name : "Click to choose a PDF"}</span>
                <span className="esigndrophint sub">The BoldSign builder opens right here.</span>
              </label>

              {err ? <div className="esignerr">{err}</div> : null}

              <div className="expformacts">
                <button className="btn slim primary" disabled={!canSend} onClick={send}>
                  {busy ? "Preparing…" : "Send for Signature"}
                </button>
                <button className="btn slim" disabled={busy} onClick={onClose}>Cancel</button>
              </div>
            </div>
          ) : null}

          {phase.kind === "embed" ? (
            <iframe className="esignframe" src={phase.url} title="BoldSign document builder" allow="fullscreen" />
          ) : null}
        </div>
      </div>
    </div>
  );
}
